import moment, {Moment} from 'moment';

const EPOCH_MILLIS = 'epochMillis';
const DISPLAY_TABLE_FORMAT = 'DD-MM-YYYY HH:mm:ss';
const ZONE_ID_PATTERN = /\[([^\]]+)\]$/;
const ENGLISH_LOCALE = 'en';

const FALLBACK_PATTERNS: string[] = [
  "yyyy-MM-dd'T'HH:mm:ss.SSSSSSSSSZ",
  "yyyy-MM-dd'T'HH:mm:ss.SSSSSSZ",
  "yyyy-MM-dd'T'HH:mm:ss.SSSZ",
  "yyyy-MM-dd'T'HH:mm:ssZ",
  "yyyy-MM-dd'T'HH:mm:ssXXX",
  "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
  "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXX",
  "yyyy-MM-dd'T'HH:mm:ss.SSSSSSSSSXXX",
  "yyyy-MM-dd'T'HH:mm:ss",
  'yyyy-MM-dd',
  'dd/MM/yyyy',
  'dd-MM-yyyy HH:mm:ss',
  'yyyy-MM-dd HH:mm:ss',
  'MM/dd/yyyy',
  'EEE, dd MMM yyyy HH:mm:ss',
  'dd-MM-yyyy HH:mm:ss XXX',
  'dd-MM-yyyy HH:mm:ss XX',
  'dd-MM-yyyy HH:mm:ss X',
  "dd-MM-yyyy HH:mm:ss 'UTC'",
  'dd-MM-yyyy HH:mm:ss z',
];

class DateFormatUtil {
  private toMomentPattern(pattern: string): string {
    return this.convertPattern(pattern);
  }

  private hasTimezoneToken(pattern: string): boolean {
    if (this.isUtcLiteralFormat(pattern)) {
      return true;
    }

    const resolved = pattern;
    let index = 0;
    while (index < resolved.length) {
      const char = resolved.charAt(index);
      if (char === "'") {
        const closing = resolved.indexOf("'", index + 1);
        if (closing === -1) {
          break;
        }
        index = closing + 1;
        continue;
      }
      if (char === 'X' || char === 'Z' || char === 'z') {
        return true;
      }
      index += 1;
    }
    return false;
  }

  private parseValue(value: string | number, pattern?: string): Moment | undefined {
    if (value == null || value === '') {
      return undefined;
    }

    const format = pattern || EPOCH_MILLIS;
    if (format === EPOCH_MILLIS) {
      return this.parseEpoch(value);
    }

    if (typeof value === 'number') {
      return this.parseEpoch(value);
    }

    if (typeof value !== 'string') {
      return undefined;
    }

    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return undefined;
    }

    if (/^-?\d+$/.test(trimmed)) {
      return this.parseEpoch(Number(trimmed));
    }

    if (this.isUtcLiteralFormat(format)) {
      return this.parseUtcValue(trimmed, format);
    }

    const treatAsLocal = !this.hasTimezoneToken(format);
    const primary = this.parseWithPattern(trimmed, format, treatAsLocal);
    if (primary != undefined && primary.isValid()) {
      return primary;
    }

    for (const fallback of FALLBACK_PATTERNS) {
      const parsed = this.parseWithPattern(trimmed, fallback, treatAsLocal);
      if (parsed != undefined && parsed.isValid()) {
        return parsed;
      }
    }

    const zoneStripped = this.stripZoneId(trimmed);
    if (treatAsLocal) {
      const withOffset = moment.parseZone(zoneStripped);
      if (withOffset.isValid()) {
        return moment([
          withOffset.year(),
          withOffset.month(),
          withOffset.date(),
          withOffset.hour(),
          withOffset.minute(),
          withOffset.second(),
          withOffset.millisecond(),
        ]);
      }
      const isoLocal = moment(zoneStripped, moment.ISO_8601, true);
      return isoLocal.isValid() ? isoLocal : undefined;
    }

    const isoParsed = moment.parseZone(zoneStripped);
    return isoParsed.isValid() ? isoParsed : undefined;
  }

  public formatForTable(value: unknown, pattern?: string): string {
    if (value == null || value === '') {
      return '';
    }

    const format = pattern || EPOCH_MILLIS;
    if (format === EPOCH_MILLIS) {
      return this.formatEpochForDisplay(value);
    }

    const parsed = this.parseValue(value as string | number, format);
    if (parsed == undefined || !parsed.isValid()) {
      return String(value);
    }

    return this.formatTableClock(parsed, format);
  }

  private formatTableClock(parsed: Moment, format: string): string {
    if (this.isUtcLiteralFormat(format)) {
      return parsed.clone().utc().format(DISPLAY_TABLE_FORMAT);
    }

    return parsed.format(DISPLAY_TABLE_FORMAT);
  }

  public formatEpochForDisplay(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }

    const parsed = this.parseEpoch(value as string | number);
    if (parsed == undefined || !parsed.isValid()) {
      return String(value);
    }
    return parsed.format(DISPLAY_TABLE_FORMAT);
  }

  public formatForTooltip(value: unknown, pattern?: string): string {
    if (value == null || value === '') {
      return '';
    }

    const format = pattern || EPOCH_MILLIS;

    // Already in channel format
    if (typeof value === 'string' && !/^-?\d+$/.test(value.trim())) {
      return value.trim();
    }

    if (format === EPOCH_MILLIS) {
      return String(value);
    }

    const parsed = this.parseValue(value as string | number, format);
    if (parsed == undefined || !parsed.isValid()) {
      return String(value);
    }

    return this.formatWithChannelPattern(parsed, format, value);
  }

  private formatWithChannelPattern(parsed: Moment, format: string, originalValue: unknown): string {
    const momentFormat = this.toMomentPattern(format);
    let formatted: string;
    if (this.isUtcLiteralFormat(format)) {
      formatted = this.withEnglishLocale(parsed.clone().utc()).format(momentFormat);
    } else if (this.hasZoneNameToken(format)) {
      formatted = this.appendZoneAbbreviation(this.withEnglishLocale(parsed).format(momentFormat), parsed);
    } else if (this.hasTimezoneToken(format)) {
      formatted = this.withEnglishLocale(parsed).format(momentFormat);
    } else {
      formatted = this.withEnglishLocale(parsed).format(momentFormat);
    }

    return this.normalizeGeneratedOffset(format, formatted);
  }

  public generateSampleValue(pattern: string): string | number {
    const format = pattern || EPOCH_MILLIS;
    if (format === EPOCH_MILLIS) {
      return Date.now();
    }
    const nowLocal = moment();

    // 'UTC' literal and similar: emit real UTC wall-clock, not local labeled as UTC.
    if (this.isUtcLiteralFormat(format)) {
      return this.withEnglishLocale(nowLocal.clone().utc()).format(this.toMomentPattern(format));
    }

    const source = this.hasZoneNameToken(format) ? nowLocal.clone().utc() : nowLocal;
    let formatted = this.withEnglishLocale(source).format(this.toMomentPattern(format));
    formatted = this.normalizeGeneratedOffset(format, formatted);
    formatted = this.normalizeGeneratedZoneName(format, formatted);
    return formatted;
  }

  private isUtcLiteralFormat(pattern: string): boolean {
    return /'UTC'/.test(pattern);
  }

  private hasZoneNameToken(pattern: string): boolean {
    const withoutQuotes = pattern.replace(/'[^']*'/g, '');
    return /z/.test(withoutQuotes);
  }

  private hasTextualDateToken(pattern: string): boolean {
    const withoutQuotes = pattern.replace(/'[^']*'/g, '');
    return /E{1,4}|M{3,4}|a/.test(withoutQuotes);
  }

  private withEnglishLocale(value: Moment): Moment {
    return value.locale(ENGLISH_LOCALE);
  }

  private parseUtcValue(trimmed: string, format: string): Moment | undefined {
    const withoutUtcSuffix = trimmed.replace(/\s*UTC$/i, '').trim();
    const momentFormat = this.toMomentPattern(format).replace('[UTC]', '').trim();
    let parsed = moment.utc(withoutUtcSuffix, momentFormat, true);
    if (!parsed.isValid()) {
      parsed = moment.utc(withoutUtcSuffix, moment.ISO_8601, true);
    }
    if (!parsed.isValid()) {
      const withOffset = moment.parseZone(withoutUtcSuffix);
      if (withOffset.isValid()) {
        parsed = moment.utc([
          withOffset.year(),
          withOffset.month(),
          withOffset.date(),
          withOffset.hour(),
          withOffset.minute(),
          withOffset.second(),
          withOffset.millisecond(),
        ]);
      }
    }
    return parsed.isValid() ? parsed : undefined;
  }

  private normalizeGeneratedOffset(pattern: string, formatted: string): string {
    // Single X expects short offset (+03); moment ZZ emits +0300.
    if (!/(^|[^X])X([^X]|$)/.test(pattern) || /XX/.test(pattern)) {
      return formatted;
    }
    return formatted.replace(/([+-]\d{2})(\d{2})$/, '$1');
  }

  private normalizeGeneratedZoneName(pattern: string, formatted: string): string {
    if (!this.hasZoneNameToken(pattern)) {
      return formatted;
    }
    if (/\sUTC$/i.test(formatted)) {
      return formatted;
    }
    const withoutTrailingZone = formatted.replace(/\s+[A-Za-z]{2,5}$/, '').replace(/\s+GMT[+-]\d+$/i, '').trimEnd();
    return `${withoutTrailingZone} UTC`;
  }

  private appendZoneAbbreviation(formatted: string, parsed: Moment): string {
    if (/\sUTC$/i.test(formatted) || /\sGMT[+-]?\d*$/i.test(formatted) || /\s[A-Z]{2,5}$/.test(formatted)) {
      return formatted;
    }
    const parts = new Intl.DateTimeFormat('en-US', {timeZoneName: 'short'}).formatToParts(parsed.toDate());
    const zonePart = parts.find((part) => part.type === 'timeZoneName');
    return `${formatted.trimEnd()} ${zonePart?.value || 'UTC'}`;
  }

  private convertPattern(pattern: string): string {
    const hasMeridiem = /a/.test(pattern.replace(/'[^']*'/g, ''));
    let result = '';
    let index = 0;

    while (index < pattern.length) {
      const char = pattern.charAt(index);

      if (char === "'") {
        const closing = pattern.indexOf("'", index + 1);
        if (closing === -1) {
          result += `[${pattern.slice(index + 1)}]`;
          break;
        }
        const literal = pattern.slice(index + 1, closing);
        result += `[${literal}]`;
        index = closing + 1;
        continue;
      }

      let runLength = 1;
      while (index + runLength < pattern.length && pattern.charAt(index + runLength) === char) {
        runLength += 1;
      }

      result += this.mapToken(char === 'h' && !hasMeridiem ? 'H' : char, runLength);
      index += runLength;
    }

    return result;
  }

  private mapToken(token: string, length: number): string {
    switch (token) {
      case 'y':
        return length >= 4 ? 'YYYY' : 'YY';
      case 'M':
        if (length >= 4) return 'MMMM';
        if (length === 3) return 'MMM';
        return 'MM'.slice(0, Math.min(length, 2));
      case 'd':
        return length >= 2 ? 'DD' : 'D';
      case 'E':
        return length >= 4 ? 'dddd' : 'ddd';
      case 'H':
        return 'H'.repeat(Math.min(length, 2));
      case 'h':
        return 'h'.repeat(Math.min(length, 2));
      case 'm':
        return 'm'.repeat(Math.min(length, 2));
      case 's':
        return 's'.repeat(Math.min(length, 2));
      case 'S':
        return 'S'.repeat(Math.min(length, 9));
      case 'X':
        if (length >= 3) return 'Z';
        return 'ZZ';
      case 'Z':
        return 'ZZ';
      case 'z':
        return 'z';
      case 'a':
        return 'A';
      default:
        return token.repeat(length);
    }
  }

  private parseWithPattern(value: string, pattern: string, treatAsLocal: boolean): Moment | undefined {
    const zoneId = this.extractZoneId(value);
    const valueWithoutZone = this.stripZoneId(value);
    const momentFormat = this.toMomentPattern(pattern);
    const candidates = this.buildParseCandidates(valueWithoutZone);
    const useEnglish = this.hasTextualDateToken(pattern) || this.hasZoneNameToken(pattern);

    let parsed: Moment = moment.invalid();
    for (const candidate of candidates) {
      if (treatAsLocal) {
        parsed = useEnglish ? moment(candidate, momentFormat, ENGLISH_LOCALE, true) : moment(candidate, momentFormat, true);
      } else if (this.hasZoneNameToken(pattern)) {
        parsed = this.parseWithZoneName(candidate, momentFormat);
      } else {
        parsed = useEnglish ? moment.parseZone(candidate, momentFormat, ENGLISH_LOCALE, true) : moment.parseZone(candidate, momentFormat, true);
      }
      if (parsed.isValid()) {
        break;
      }
    }

    if (!parsed.isValid() && treatAsLocal) {
      const withOffset = moment.parseZone(valueWithoutZone);
      if (withOffset.isValid()) {
        parsed = moment([
          withOffset.year(),
          withOffset.month(),
          withOffset.date(),
          withOffset.hour(),
          withOffset.minute(),
          withOffset.second(),
          withOffset.millisecond(),
        ]);
      }
    }

    if (!parsed.isValid() && !treatAsLocal && this.hasZoneNameToken(pattern)) {
      parsed = this.parseWithZoneName(valueWithoutZone, momentFormat);
    }

    if (!parsed.isValid() && !treatAsLocal) {
      const isoLike = moment.parseZone(valueWithoutZone, moment.ISO_8601, true);
      if (isoLike.isValid()) {
        parsed = isoLike;
      }
    }

    return parsed.isValid() ? parsed : undefined;
  }

  private parseWithZoneName(value: string, momentFormat: string): Moment {
    const utcMatch = value.match(/^(.*)\s+UTC$/i);
    if (utcMatch != undefined) {
      const baseFormat = momentFormat.replace(/\s*z+$/, '').trim();
      const parsedUtc = moment.utc(utcMatch[1].trim(), baseFormat, ENGLISH_LOCALE, true);
      if (parsedUtc.isValid()) {
        return parsedUtc;
      }
    }

    const gmtMatch = value.match(/^(.*)\s+GMT([+-]\d{1,2})$/i);
    if (gmtMatch != undefined) {
      const baseFormat = momentFormat.replace(/\s*z+$/, '').trim();
      const offsetHours = Number(gmtMatch[2]);
      const wall = moment.utc(gmtMatch[1].trim(), baseFormat, ENGLISH_LOCALE, true);
      if (wall.isValid()) {
        return wall.subtract(offsetHours, 'hours');
      }
    }

    const baseFormat = momentFormat.replace(/\s*z+$/, '').trim();
    const withoutZoneName = value.replace(/\s+[A-Za-z]{2,5}$/, '').trim();
    return moment.utc(withoutZoneName, baseFormat, ENGLISH_LOCALE, true);
  }

  private buildParseCandidates(value: string): string[] {
    const candidates = [value];
    const withColon = value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    const withoutColon = value.replace(/([+-]\d{2}):(\d{2})$/, '$1$2');
    if (withColon !== value) {
      candidates.push(withColon);
    }
    if (withoutColon !== value) {
      candidates.push(withoutColon);
    }
    return candidates;
  }

  private parseEpoch(value: string | number): Moment | undefined {
    let numeric: number;
    if (typeof value === 'number') {
      numeric = value;
    } else if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
      numeric = Number(value.trim());
    } else {
      return undefined;
    }

    if (!Number.isFinite(numeric)) {
      return undefined;
    }

    const parsed = moment(numeric);
    return parsed.isValid() ? parsed : undefined;
  }

  private extractZoneId(value: string): string | undefined {
    const match = value.match(ZONE_ID_PATTERN);
    return match != undefined ? match[1] : undefined;
  }

  private stripZoneId(value: string): string {
    return value.replace(ZONE_ID_PATTERN, '');
  }
}

export default new DateFormatUtil();
